import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { isNum, pct, useFetch, Status } from './helpers';
import './PlusFundReport.css';

// Monthly report generator: the backend (report_docx.py) fills the bundled report
// template (plus_fund/templates, the September 2026 report) with the latest month's
// tables and native chart data and returns this month's document. Commentary (and a look-through not entered
// for this month) comes back highlighted for editing in Word.
const TILT_ROWS = ['Australian Equities', 'Global Equities', 'Real Estate', 'Infrastructure', 'Government Bonds', 'Corporate Debt'];
// Prefill until saved: the September 2026 report's tilts (the house view carries forward).
const SEPT_2026_TILTS = {
  'Australian Equities': 'Underweight', 'Global Equities': 'Overweight', 'Real Estate': 'Neutral',
  Infrastructure: 'Overweight', 'Government Bonds': 'Underweight', 'Corporate Debt': 'Overweight',
};

function parsePasted(text) {
  return text.split(/\r?\n/).map((line) => line.split('\t').map((c) => c.trim()).filter(Boolean)).filter((c) => c.length >= 2)
    .map((cells) => {
      const raw = cells[cells.length - 1];
      let w = parseFloat(raw.replace('%', ''));
      if (raw.endsWith('%') || w >= 1) w /= 100;
      const name = cells.slice(0, -1).find((c) => Number.isNaN(parseFloat(c))) || cells[0];
      return { name, weight: w };
    })
    .filter((r) => isNum(r.weight)).slice(0, 10);
}

// FastAPI errors arrive as a Blob (responseType 'blob'); detail is a string or, for a
// request validation error, a list of {loc, msg}.
async function blobError(e) {
  let d = e.response?.data;
  if (d instanceof Blob) {
    try { d = JSON.parse(await d.text()); } catch { return e.message; }
  }
  const det = d?.detail;
  if (Array.isArray(det)) return det.map((x) => `${(x.loc || []).join('.')}: ${x.msg}`).join('; ');
  return det || e.message;
}

export default function ReportTab() {
  const perf = useFetch('/api/plus-fund/performance');
  const dates = useFetch('/api/plus-fund/report-holdings/dates');
  const asAt = perf.data?.as_at;
  const month = asAt?.slice(0, 7);
  const [hDate, setHDate] = useState('');
  useEffect(() => { if (asAt && !hDate) setHDate(asAt); }, [asAt, hDate]);
  const holdings = useFetch('/api/plus-fund/report-holdings', { date: hDate }, Boolean(hDate));

  const [inputs, setInputs] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [paste, setPaste] = useState('');
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get('/api/plus-fund/inputs/report').then((r) => {
      const v = r.data?.value;
      setInputs({ tilts: v?.tilts || SEPT_2026_TILTS, top10: v?.top10 || [], top10_month: v?.top10_month || null });
      setSavedAt(r.data?.updated_at ? `${r.data.updated_at.slice(0, 10)} by ${r.data.updated_by}` : null);
    }).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);
  const edit = (patch) => { setInputs((s) => ({ ...s, ...patch })); setDirty(true); };
  const editTop = (top10) => edit({ top10, top10_month: month });
  const save = () => api.put('/api/plus-fund/inputs/report', { value: inputs })
    .then(() => { setDirty(false); setErr(null); setSavedAt('just now'); })
    .catch((e) => setErr(e.response?.data?.detail || e.message));

  const generate = async () => {
    setBusy(true); setErr(null); setNotes(null);
    try {
      if (dirty) await api.put('/api/plus-fund/inputs/report', { value: inputs }).then(() => { setDirty(false); setSavedAt('just now'); });
      const r = await api.get('/api/plus-fund/report-docx', { params: hDate ? { holdings_date: hDate } : {}, responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const name = (cd.match(/filename="([^"]+)"/) || [])[1] || 'Dynamic Plus Fund report.docx';
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      setNotes(JSON.parse(r.headers['x-report-notes'] || '[]'));
    } catch (e) {
      setErr(await blobError(e));
    } finally { setBusy(false); }
  };

  if (!perf.data) return <Status loading={perf.loading} error={perf.error} />;
  const reportMonth = new Date(asAt).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  const topCurrent = inputs?.top10?.length && inputs.top10_month === month;
  const h = holdings.data;

  return (
    <div className="pfr-wrap">
      <div className="pf-note">
        Downloads the {reportMonth} report as a Word document, built from the standard report template (the September 2026
        layout): every table and native Word chart is filled with portal data to {asAt} and the month is set in the title,
        header and Fund Positioning heading. The commentary comes back highlighted for rewriting, as does the look-through
        if none is entered for {reportMonth}. Positioning, tilt totals and FX use the holdings date below (the
        September report used 2 October holdings); Strategy FUM is fund + separate account NAV at {asAt}.
      </div>

      <div className="pfr-grid">
        <section className="pfr-box">
          <h3>1. Holdings date</h3>
          <select value={hDate} onChange={(e) => setHDate(e.target.value)}>
            {[asAt, ...(dates.data?.dates || []).filter((d) => d !== asAt)].filter(Boolean).map((d) => <option key={d} value={d}>{d}{d === asAt ? ' (month end)' : ''}</option>)}
          </select>
          <Status loading={holdings.loading} error={holdings.error || dates.error} />
          {h && (
            <table className="pfr-mini">
              <tbody>
                {h.date !== hDate && <tr><td colSpan={2} className="pf-muted">no IB file on {hDate}; using {h.date}</td></tr>}
                {h.positioning.map((p) => <tr key={p.label}><td>{p.label}</td><td>{pct(p.weight, 1)}</td></tr>)}
                <tr><td>Gross Exposure</td><td>{pct(h.gross, 1)}</td></tr>
                <tr><td>FX Exposure (non-AUD)</td><td>{pct(h.non_aud, 1)}</td></tr>
                <tr><td>Total Growth / Defensive</td><td>{pct(h.growth, 1)} / {pct(h.defensive, 1)}</td></tr>
              </tbody>
            </table>
          )}
        </section>

        {inputs && (
          <section className="pfr-box">
            <h3>2. Asset class tilts</h3>
            <div className="pfr-tilt-edit">
              {TILT_ROWS.map((r) => (
                <label key={r}>{r}
                  <select value={inputs.tilts[r] || ''} onChange={(e) => edit({ tilts: { ...inputs.tilts, [r]: e.target.value } })}>
                    {['Underweight', 'Neutral', 'Overweight'].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </section>
        )}

        {inputs && (
          <section className="pfr-box">
            <h3>3. Top equity holdings look through</h3>
            <div className={topCurrent ? 'pf-saved' : 'pf-muted'}>
              {topCurrent ? `Entered for ${reportMonth}` : inputs.top10?.length ? `Entered for ${inputs.top10_month}, not ${reportMonth}: the template's rows will be kept and highlighted` : `Not entered: the template's rows will be kept and highlighted`}
            </div>
            <div className="pf-muted">Paste the name and weight columns from the Top Holdings sheet of the look-through workbook (tab separated; 0.031 or 3.1% both work), then tidy names to the report style.</div>
            <textarea rows={3} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={'BHP Group\t3.1%'} />
            <button className="pf-btn pf-btn-light" onClick={() => { const rows = parsePasted(paste); if (rows.length) { editTop(rows); setPaste(''); } }}>Use pasted rows</button>
            {inputs.top10.map((r, i) => (
              <div key={i} className="pfr-kv">
                <input value={r.name} onChange={(e) => editTop(inputs.top10.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <input value={isNum(r.weight) ? +(r.weight * 100).toFixed(2) : ''} onChange={(e) => editTop(inputs.top10.map((x, j) => (j === i ? { ...x, weight: parseFloat(e.target.value) / 100 } : x)))} />
              </div>
            ))}
          </section>
        )}

        <section className="pfr-box">
          <h3>4. Generate</h3>
          <div className="pfr-actions">
            <button className="pf-btn" disabled={busy || !inputs} onClick={generate}>{busy ? 'Building…' : `Download ${reportMonth} report`}</button>
            <button className="pf-btn pf-btn-light" disabled={!dirty} onClick={save}>Save inputs</button>
          </div>
          <div className="pf-muted">{dirty ? 'Unsaved changes (saved automatically on generate)' : savedAt ? `Inputs saved ${savedAt}` : 'Tilts prefilled from the September 2026 report (not yet saved)'}</div>
          {err && <div className="pf-alert">{String(err)}</div>}
          {notes && <ul className="pfr-notes">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
        </section>
      </div>
    </div>
  );
}
